import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import {
    GetSlotsResponse, SaptamanaUI, SpecialistSaptamanaUI,
    ZiSaptamanaUI, SelectieSaptamana, SelectieSedinta, SlotDisponibil,
    PreselectieSedinta
} from './disponibilitate.interface';
import { InregistrareProgramareService } from '../inregistrare-programare.service';
import Swal from 'sweetalert2';
import { ConfirmaSelectieEvent, ConfirmaSelectieSlot } from '../inregistrare-programare.interface';

interface SpecialistOption {
    specialist_id: number;
    nume_specialist: string;
}

@Component({
    selector: 'app-disponibilitate-programare',
    templateUrl: './disponibilitate-programare.component.html',
    styleUrls: ['./disponibilitate-programare.component.scss']
})
export class DisponibilitateProgramareComponent implements OnChanges {

    @Input() slotsResponse: GetSlotsResponse | null = null;
    @Input() nrSedinte: number = 1;
    @Input() frecventa: number = 1;
    @Input() preselectii: PreselectieSedinta[] = [];
    @Input() contractId: number | null = null;
    @Input() dateEndSedinte: string | null = null;
    @Output() confirmaSelectie = new EventEmitter<ConfirmaSelectieEvent>();
    @Output() selectiiChange = new EventEmitter<{ slot_id: number; date: string }[]>();

    saptamani: SaptamanaUI[] = [];
    selectii: Map<number, SelectieSaptamana> = new Map();
    activeSedintaIndex: Map<number, number> = new Map();

    // Filtru specialist
    specialistiOptions: SpecialistOption[] = [];
    selectedSpecialistId: number = -1; // -1 = toți

    readonly DAY_LABELS: { [key: number]: string } = {
        1: 'Luni', 2: 'Marți', 3: 'Miercuri',
        4: 'Joi', 5: 'Vineri', 6: 'Sâmbătă', 7: 'Duminică'
    };

    constructor(private inregistrareProgramareService: InregistrareProgramareService) { }


    ngOnChanges(changes: SimpleChanges): void {
        if (changes['slotsResponse'] && this.slotsResponse) {
            this.buildSpecialistiOptions();
            this.buildSaptamani();

            if (this.preselectii?.length) {
                this.restorePreselectii();
            }
            return;
        }

        // Detectăm schimbarea nrSedinte DUPĂ ce avem deja slotsResponse
        // (adică suntem în modul editare și utilizatorul a modificat câmpul)
        if (changes['nrSedinte'] && !changes['nrSedinte'].firstChange && this.slotsResponse) {
            const selectiiSalvate = new Map(this.selectii);
            this.buildSpecialistiOptions();
            this.saptamani = [];
            this.selectii = new Map();
            this.activeSedintaIndex = new Map();

            const toateDatele = this.getAllDates();
            if (toateDatele.length) {
                const startOfWeek = this.getMondayOfWeek(new Date(toateDatele[0]));
                if (this.preselectii?.length) {
                    this.buildSaptamaniPentruEdit(startOfWeek);
                    this.restorePreselectii();
                } else {
                    const nrSaptamani = Math.ceil(this.nrSedinte / this.frecventa);
                    this.generateSaptamani(startOfWeek, nrSaptamani);
                }
            }

            // Restaurăm selectiile compatibile
            for (const [weekIndex, sel] of selectiiSalvate) {
                if (this.saptamani.some(s => s.weekIndex === weekIndex && !s.isSkipped)) {
                    this.selectii.set(weekIndex, sel);
                }
            }

            this.emitSelectiiChange();
        }
    }

    restorePreselectii(): void {
        for (const pre of this.preselectii) {
            const preDate = new Date(pre.date + 'T00:00:00');
            preDate.setHours(0, 0, 0, 0);

            const sapt = this.saptamani.find(s => {
                const start = new Date(s.dateStart);
                start.setHours(0, 0, 0, 0);
                const end = new Date(s.dateEnd);
                end.setHours(23, 59, 59, 999);
                return preDate >= start && preDate <= end;
            });

            if (!sapt) {
                console.warn('[restore] Săptămâna negăsită pentru:', pre.date);
                continue;
            }

            // Dacă săptămâna era marcată isSkipped dar are preselectii, o scoatem din skip
            if (sapt.isSkipped) {
                sapt.isSkipped = false;
            }

            const specDisponibil = sapt.specialistiDisponibili.find(
                s => s.specialist_id === pre.specialist_id
            );
            const locatieId = specDisponibil?.locatie_id ?? 0;

            const slotReconstruit: SlotDisponibil = {
                slot_id: pre.slot_id,
                time_from: pre.time_from,
                time_end: '',
                duration: 0,
            };

            let selExistenta = this.selectii.get(sapt.weekIndex);
            if (!selExistenta) {
                selExistenta = { weekIndex: sapt.weekIndex, sedinte: [] };
            }

            const sedintaIdx = selExistenta.sedinte.length;

            selExistenta.sedinte.push({
                sedintaIndex: sedintaIdx,
                specialist_id: pre.specialist_id,
                locatie_id: locatieId,
                date: pre.date,
                slot: slotReconstruit,
                nume_specialist: pre.nume_specialist,
            });

            this.selectii.set(sapt.weekIndex, selExistenta);
        }

        this.emitSelectiiChange();
    }

    // ─── Filtru specialist ────────────────────────────────────────────────────

    buildSpecialistiOptions(): void {
        const seen = new Set<number>();
        this.specialistiOptions = [];

        for (const loc of this.slotsResponse!.data.locatii) {
            for (const spec of loc.specialisti) {
                if (!seen.has(spec.specialist_id)) {
                    seen.add(spec.specialist_id);
                    this.specialistiOptions.push({
                        specialist_id: spec.specialist_id,
                        nume_specialist: spec.nume_specialist ?? `Specialist ${spec.specialist_id}`
                    });
                }
            }
        }
    }

    onSpecialistFilterChange(): void {
        for (const sapt of this.saptamani) {
            if (this.isComplete(sapt)) continue;
            sapt.specialistiDisponibili = this.getSpecialistiForWeek(sapt.dateStart, sapt.dateEnd);
        }
    }

    // ─── Selecție automată ────────────────────────────────────────────────────

    /**
     * Prima săptămână complet configurată (toate frecventa ședințele selectate)
     * și care nu e sărită — folosită ca template pentru selectie automată.
     */
    get primaSaptamanaCompleta(): SaptamanaUI | null {
        return this.saptamani.find(s => !s.isSkipped && this.isComplete(s)) ?? null;
    }

    get canSelectieAutomata(): boolean {
        return this.primaSaptamanaCompleta !== null;
    }

    async onSelectieAutomata(): Promise<void> {
        const template = this.primaSaptamanaCompleta;
        if (!template) return;

        const templateSelectie = this.selectii.get(template.weekIndex);
        if (!templateSelectie) return;

        // Câte săptămâni rămân de configurat (necompletate și nesărite)
        const saptyRamase = this.saptamani.filter(
            s => !s.isSkipped && !this.isComplete(s) && s.weekIndex !== template.weekIndex
        );

        if (saptyRamase.length === 0) {
            Swal.fire({
                icon: 'info',
                title: 'Nimic de configurat',
                text: 'Toate săptămânile sunt deja configurate.',
                confirmButtonColor: '#1976d2'
            });
            return;
        }

        const result = await Swal.fire({
            icon: 'question',
            title: 'Selecție automată',
            html: `
                Se vor selecta automat sloturile pentru
                <strong>${saptyRamase.length}</strong> săptămân${saptyRamase.length === 1 ? 'ă rămasă' : 'i rămase'},
                folosind aceleași zile și ore ca în
                <strong>${template.weekLabel}</strong>.<br><br>
                Săptămânile sărite vor fi ignorate.
                Ești sigur?
            `,
            showCancelButton: true,
            confirmButtonText: 'Da, selectează automat',
            cancelButtonText: 'Anulează',
            confirmButtonColor: '#1976d2',
            cancelButtonColor: '#9e9e9e',
            reverseButtons: true,
        });

        if (!result.isConfirmed) return;

        this.aplicaSelectieAutomata(template, templateSelectie, saptyRamase);
    }

    aplicaSelectieAutomata(
        template: SaptamanaUI,
        templateSelectie: SelectieSaptamana,
        saptyRamase: SaptamanaUI[]
    ): void {
        const toateSedinteleNoi: { sedinta: SelectieSedinta; action: 'add' }[] = [];

        for (const sapt of saptyRamase) {
            const newSedinte: SelectieSedinta[] = [];

            for (const templateSed of templateSelectie.sedinte) {
                // Găsim ziua echivalentă în săptămâna curentă (același day_of_week)
                const targetZi = this.getZiByDayOfWeek(sapt, templateSed);
                if (!targetZi) continue;

                // Găsim același slot_id sau cel mai apropiat ca oră
                const targetSlot = this.getMatchingSlot(targetZi, templateSed.slot);
                if (!targetSlot) continue;

                // Găsim specialistul corespunzător (același id dacă există, altfel primul disponibil)
                const targetSpec = this.getMatchingSpecialist(sapt, templateSed.specialist_id, targetZi.date);
                if (!targetSpec) continue;

                const newSedinta: SelectieSedinta = {
                    sedintaIndex: templateSed.sedintaIndex,
                    specialist_id: targetSpec.specialist_id,
                    locatie_id: targetSpec.locatie_id,
                    date: targetZi.date,
                    slot: targetSlot,
                    nume_specialist: targetSpec.nume_specialist,
                };

                newSedinte.push(newSedinta);
                toateSedinteleNoi.push({ sedinta: newSedinta, action: 'add' });
            }

            if (newSedinte.length > 0) {
                this.selectii.set(sapt.weekIndex, {
                    weekIndex: sapt.weekIndex,
                    sedinte: newSedinte
                });
            }
        }

        // Un singur apel updateSlots cu toate sloturile noi
        if (toateSedinteleNoi.length > 0) {
            this.callUpdateSlots(toateSedinteleNoi);
        }

        this.emitSelectiiChange();
    }

    /** Găsește ziua din săptămâna target cu același day_of_week ca ședința template */
    getZiByDayOfWeek(sapt: SaptamanaUI, templateSed: SelectieSedinta): ZiSaptamanaUI | null {
        // Determinăm day_of_week al datei template
        const templateDate = new Date(templateSed.date + 'T00:00:00');
        const jsDay = templateDate.getDay(); // 0=Sun
        const targetDayOfWeek = jsDay === 0 ? 7 : jsDay;

        for (const spec of sapt.specialistiDisponibili) {
            const zi = spec.zileDisponibile.find(z => z.day_of_week === targetDayOfWeek);
            if (zi) return zi;
        }
        return null;
    }

    /** Găsește slotul cu același slot_id, sau cel cu același time_from */
    getMatchingSlot(zi: ZiSaptamanaUI, templateSlot: SlotDisponibil): SlotDisponibil | null {
        return zi.slots.find(s => s.slot_id === templateSlot.slot_id)
            ?? zi.slots.find(s => s.time_from === templateSlot.time_from)
            ?? null;
    }

    /** Găsește specialistul cu același id, sau primul care are acea zi disponibilă */
    getMatchingSpecialist(sapt: SaptamanaUI, specialistId: number, targetDate: string): SpecialistSaptamanaUI | null {
        return sapt.specialistiDisponibili.find(
            s => s.specialist_id === specialistId && s.zileDisponibile.some(z => z.date === targetDate)
        ) ?? sapt.specialistiDisponibili.find(
            s => s.zileDisponibile.some(z => z.date === targetDate)
        ) ?? null;
    }

    // ─── Build săptămâni ──────────────────────────────────────────────────────

    buildSaptamani(forceRebuild = false): void {
        if (!this.slotsResponse?.data?.locatii?.length) return;

        const toateDatele = this.getAllDates();
        if (!toateDatele.length) return;

        const startOfWeek = this.getMondayOfWeek(new Date(toateDatele[0]));

        // Păstrăm selectiile existente dacă facem rebuild (modul editare cu nr_sedinte schimbat)
        const selectiiSalvate = forceRebuild ? new Map(this.selectii) : null;

        this.saptamani = [];
        this.selectii = new Map();
        this.activeSedintaIndex = new Map();

        if (this.preselectii?.length) {
            this.buildSaptamaniPentruEdit(startOfWeek);
        } else {
            const nrSaptamani = Math.ceil(this.nrSedinte / this.frecventa);
            this.generateSaptamani(startOfWeek, nrSaptamani);
        }

        // Restaurăm selectiile salvate (pentru rebuild cu nr_sedinte modificat)
        if (selectiiSalvate && forceRebuild) {
            for (const [weekIndex, sel] of selectiiSalvate) {
                if (this.saptamani.some(s => s.weekIndex === weekIndex)) {
                    this.selectii.set(weekIndex, sel);
                }
            }
        }
    }

    private buildSaptamaniPentruEdit(startOfWeek: Date): void {
        const datePreselectii = this.preselectii
            .map(p => new Date(p.date + 'T00:00:00'))
            .sort((a, b) => a.getTime() - b.getTime());

        const lastDate = datePreselectii[datePreselectii.length - 1];
        const lastWeekStart = this.getMondayOfWeek(lastDate);

        const msPerWeek = 7 * 24 * 60 * 60 * 1000;
        const nrSaptamaniAcoperite = Math.round(
            (lastWeekStart.getTime() - startOfWeek.getTime()) / msPerWeek
        ) + 1;

        const nrSaptamaniTeoretice = Math.ceil(this.nrSedinte / this.frecventa);
        const nrSaptamani = Math.max(nrSaptamaniAcoperite, nrSaptamaniTeoretice);

        this.generateSaptamani(startOfWeek, nrSaptamani);

        // Marcăm ca skip DOAR săptămânile fără preselectii
        // Dar: dacă avem mai multe săptămâni decât acoperă preselectiile
        // (pentru că nrSedinte a crescut), saptamanile extra NU primesc skip
        const nrSaptamaniCuPreselectii = this.saptamani.filter(sapt =>
            this.preselectii.some(p => {
                const preDate = new Date(p.date + 'T00:00:00');
                return preDate >= sapt.dateStart && preDate <= sapt.dateEnd;
            })
        ).length;

        // Câte sedinte noi sunt necesare față de preselectii
        const sedintePrezente = this.preselectii.length;
        const sedinteNecesare = this.nrSedinte;
        const sedinteExtra = sedinteNecesare - sedintePrezente;

        // Câte saptamani extra (fara preselectii) trebuie să rămână libere (neSkipped)
        const saptyExtraLibere = sedinteExtra > 0 ? Math.ceil(sedinteExtra / this.frecventa) : 0;

        let extraLibereRamase = saptyExtraLibere;

        for (const sapt of this.saptamani) {
            const arePreselectie = this.preselectii.some(p => {
                const preDate = new Date(p.date + 'T00:00:00');
                return preDate >= sapt.dateStart && preDate <= sapt.dateEnd;
            });

            if (arePreselectie) {
                sapt.isSkipped = false;
            } else if (extraLibereRamase > 0) {
                // Săptămână fără preselectie, dar avem nevoie de ea pentru sedinte noi
                sapt.isSkipped = false;
                extraLibereRamase--;
            } else {
                // Săptămână fără preselectie și nu mai avem nevoie de ea
                sapt.isSkipped = true;
            }
        }
    }

    generateSaptamani(startOfWeek: Date, count: number): void {
        for (let i = 0; i < count; i++) {
            const weekStart = new Date(startOfWeek);
            weekStart.setDate(weekStart.getDate() + i * 7);
            const weekEnd = new Date(weekStart);
            weekEnd.setDate(weekEnd.getDate() + 6);

            this.saptamani.push({
                weekIndex: i,
                weekLabel: this.buildWeekLabel(i, weekStart, weekEnd),
                dateStart: weekStart,
                dateEnd: weekEnd,
                isSkipped: false,
                specialistiDisponibili: this.getSpecialistiForWeek(weekStart, weekEnd)
            });
            this.activeSedintaIndex.set(i, 0);
        }
    }

    addExtraSaptamana(): void {
        const last = this.saptamani[this.saptamani.length - 1];
        const newStart = new Date(last.dateStart);
        newStart.setDate(newStart.getDate() + 7);
        const newEnd = new Date(newStart);
        newEnd.setDate(newEnd.getDate() + 6);
        const newIndex = last.weekIndex + 1;

        this.saptamani.push({
            weekIndex: newIndex,
            weekLabel: this.buildWeekLabel(newIndex, newStart, newEnd),
            dateStart: newStart,
            dateEnd: newEnd,
            isSkipped: false,
            specialistiDisponibili: this.getSpecialistiForWeek(newStart, newEnd)
        });
        this.activeSedintaIndex.set(newIndex, 0);
    }

    // ─── Helpers date ─────────────────────────────────────────────────────────

    getAllDates(): string[] {
        const dates = new Set<string>();
        for (const loc of this.slotsResponse!.data.locatii)
            for (const spec of loc.specialisti)
                for (const d of spec.dates)
                    dates.add(d.date);
        return Array.from(dates).sort();
    }

    getMondayOfWeek(date: Date): Date {
        const d = new Date(date);
        const day = d.getDay();
        d.setDate(d.getDate() + (day === 0 ? -6 : 1 - day));
        d.setHours(0, 0, 0, 0);
        return d;
    }

    getSpecialistiForWeek(weekStart: Date, weekEnd: Date): SpecialistSaptamanaUI[] {
        const result: SpecialistSaptamanaUI[] = [];
        const endInterval = this.dateEndSedinte
            ? new Date(this.dateEndSedinte + 'T23:59:59')
            : null;

        for (const loc of this.slotsResponse!.data.locatii) {
            for (const spec of loc.specialisti) {
                if (this.selectedSpecialistId !== -1 && spec.specialist_id !== this.selectedSpecialistId) {
                    continue;
                }

                const zile: ZiSaptamanaUI[] = [];
                for (const d of spec.dates) {
                    const dateObj = new Date(d.date + 'T00:00:00');

                    // FIX 3: exclude zilele după sfârșitul intervalului de ședințe
                    if (endInterval && dateObj > endInterval) continue;

                    if (dateObj >= weekStart && dateObj <= weekEnd && d.slots.length > 0) {
                        zile.push({
                            date: d.date,
                            dateFormatted: this.formatZiData(d.date, d.day_of_week),
                            day_of_week: d.day_of_week,
                            slots: [...d.slots]
                        });
                    }
                }
                if (zile.length > 0) {
                    result.push({
                        specialist_id: spec.specialist_id,
                        nume_specialist: spec.nume_specialist ?? `Specialist ${spec.specialist_id}`,
                        locatie_id: loc.locatie_id,
                        denumire_locatie: loc.denumire_locatie,
                        zileDisponibile: zile
                    });
                }
            }
        }
        return result;
    }

    getSpecialistiSelectatiForWeek(weekIndex: number): SpecialistSaptamanaUI[] {
        const sel = this.selectii.get(weekIndex);
        if (!sel?.sedinte?.length) return [];

        // Colectăm specialist_id-urile unice din selecție
        const selectedSpecialistIds = new Set(sel.sedinte.map(s => s.specialist_id));

        // Returnăm doar specialiștii din lista săptămânii care au selecții
        const sapt = this.saptamani.find(s => s.weekIndex === weekIndex);
        if (!sapt) return [];

        return sapt.specialistiDisponibili.filter(spec =>
            selectedSpecialistIds.has(spec.specialist_id)
        );
    }

    buildWeekLabel(index: number, start: Date, end: Date): string {
        return `Săpt. ${index + 1} (${this.formatShortDate(start)} – ${this.formatShortDate(end)})`;
    }

    formatShortDate(d: Date): string {
        return d.toLocaleDateString('ro-RO', { day: '2-digit', month: 'short', year: 'numeric' });
    }

    formatZiData(dateStr: string, dayOfWeek: number): string {
        const date = new Date(dateStr + 'T00:00:00');
        const dayLabel = this.DAY_LABELS[dayOfWeek] ?? '';
        const formatted = date.toLocaleDateString('ro-RO', { day: '2-digit', month: 'short' });
        return `${dayLabel}, ${formatted}`;
    }

    formatTime(time: string): string {
        return time.substring(0, 5);
    }

    // ─── Starea săptămânii ────────────────────────────────────────────────────

    hasNoSlots(sapt: SaptamanaUI): boolean {
        return sapt.specialistiDisponibili.length === 0;
    }

    isComplete(sapt: SaptamanaUI): boolean {
        const selectate = this.selectii.get(sapt.weekIndex)?.sedinte.length ?? 0;
        const maxPentruAceasta = this.getMaxSedintePentruSaptamana(sapt.weekIndex);
        return selectate >= maxPentruAceasta;
    }

    getStatusClass(sapt: SaptamanaUI): string {
        if (sapt.isSkipped) return 'week-card--skipped';
        if (this.hasNoSlots(sapt)) return 'week-card--no-slots';
        if (this.isComplete(sapt)) return 'week-card--complete';
        return 'week-card--pending';
    }

    // ─── Skip ─────────────────────────────────────────────────────────────────

    toggleSkip(sapt: SaptamanaUI): void {
        if (sapt.isSkipped) {
            // Anulează skip
            sapt.isSkipped = false;
            const last = this.saptamani[this.saptamani.length - 1];
            if (!this.selectii.has(last.weekIndex)) {
                this.saptamani.pop();
                this.activeSedintaIndex.delete(last.weekIndex);
            }
            return;
        }

        sapt.isSkipped = true;
        this.selectii.delete(sapt.weekIndex);
        this.addExtraSaptamana();
    }

    // ─── Selecție slot ────────────────────────────────────────────────────────

    getActiveSedintaIndex(weekIndex: number): number {
        return this.activeSedintaIndex.get(weekIndex) ?? 0;
    }

    selectSlot(sapt: SaptamanaUI, specialist: SpecialistSaptamanaUI, zi: ZiSaptamanaUI, slot: SlotDisponibil): void {
        if (sapt.isSkipped) return;

        const sedintaIdx = this.getActiveSedintaIndex(sapt.weekIndex);
        let sel = this.selectii.get(sapt.weekIndex) ?? { weekIndex: sapt.weekIndex, sedinte: [] };

        const existingIdx = sel.sedinte.findIndex(s => s.sedintaIndex === sedintaIdx);
        const isSameSlot = existingIdx !== -1
            && sel.sedinte[existingIdx].slot.slot_id === slot.slot_id
            && sel.sedinte[existingIdx].date === zi.date;

        if (isSameSlot) {
            const removed = sel.sedinte[existingIdx];
            sel.sedinte.splice(existingIdx, 1);
            this.selectii.set(sapt.weekIndex, sel);
            this.callUpdateSlots([{ sedinta: removed, action: 'delete' }]);
            this.emitSelectiiChange();
            return;
        }

        // ── FIX 1: Sedința 2 nu poate fi înainte de ședința 1 ────────────────────
        if (this.frecventa === 2 && sedintaIdx === 1) {
            const sedinta1 = sel.sedinte.find(s => s.sedintaIndex === 0);
            if (sedinta1 && zi.date < sedinta1.date) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Ordine invalidă',
                    text: 'Ședința 2 nu poate fi programată înainte de Ședința 1 în aceeași săptămână.',
                    confirmButtonColor: '#1976d2',
                    confirmButtonText: 'Am înțeles',
                });
                return;
            }
        }

        // ── FIX original: Bloc zi simetrică (frecventa=2, aceeași zi) ─────────────
        if (this.frecventa === 2) {
            const otherIdx = sedintaIdx === 0 ? 1 : 0;
            if (sel.sedinte.some(s => s.sedintaIndex === otherIdx && s.date === zi.date)) return;
        }

        // ── FIX 5: Nu permite depășirea numărului total de ședințe ────────────────
        const maxPentruSapt = this.getMaxSedintePentruSaptamana(sapt.weekIndex);
        const dejaSelectate = sel.sedinte.length;
        const esteInlocuire = existingIdx !== -1;
        if (!esteInlocuire && dejaSelectate >= maxPentruSapt) return;

        const newSedinta: SelectieSedinta = {
            sedintaIndex: sedintaIdx,
            specialist_id: specialist.specialist_id,
            locatie_id: specialist.locatie_id,
            date: zi.date,
            slot,
            nume_specialist: specialist.nume_specialist,
        };

        const batch: { sedinta: SelectieSedinta; action: 'add' | 'delete' }[] = [];

        if (existingIdx !== -1) {
            batch.push({ sedinta: sel.sedinte[existingIdx], action: 'delete' });
            sel.sedinte[existingIdx] = newSedinta;
        } else {
            sel.sedinte.push(newSedinta);
        }
        batch.push({ sedinta: newSedinta, action: 'add' });

        this.selectii.set(sapt.weekIndex, sel);
        this.callUpdateSlots(batch);
        this.emitSelectiiChange();

        if (this.frecventa === 2) {
            const maxAcum = this.getMaxSedintePentruSaptamana(sapt.weekIndex);
            if (sedintaIdx === 0 && sel.sedinte.length === 1 && maxAcum >= 2) {
                // Trecem automat la ședința 2 doar dacă e permisă
                this.activeSedintaIndex.set(sapt.weekIndex, 1);
            } else if (sel.sedinte.length >= maxAcum) {
                this.activeSedintaIndex.set(sapt.weekIndex, 0);
            }
        }
    }

    // ─── Remove ședință ───────────────────────────────────────────────────────

    hasAnySedinta(weekIndex: number): boolean {
        return (this.selectii.get(weekIndex)?.sedinte.length ?? 0) > 0;
    }

    removeSedinta(sapt: SaptamanaUI, sed: SelectieSedinta): void {
        const sel = this.selectii.get(sapt.weekIndex);
        if (!sel) return;

        const idx = sel.sedinte.findIndex(s => s.sedintaIndex === sed.sedintaIndex);
        if (idx === -1) return;

        sel.sedinte.splice(idx, 1);

        if (sel.sedinte.length === 0) {
            this.selectii.delete(sapt.weekIndex);
            // Săptămâna e goală → resetăm toggle-ul la ședința 1
            this.activeSedintaIndex.set(sapt.weekIndex, 0);
        } else {
            this.selectii.set(sapt.weekIndex, sel);
            if (this.frecventa === 2) {
                // Toggle-ul merge pe ședința care tocmai a fost ștearsă (ca să o poată re-selecta)
                this.activeSedintaIndex.set(sapt.weekIndex, sed.sedintaIndex);
            }
        }

        this.callUpdateSlots([{ sedinta: sed, action: 'delete' }]);
        this.reinjectSlotIfMissing(sapt, sed);

        if (this.selectedSpecialistId !== -1 && !this.isComplete(sapt)) {
            sapt.specialistiDisponibili = this.getSpecialistiForWeek(sapt.dateStart, sapt.dateEnd);
        }

        this.emitSelectiiChange();
    }

    private reinjectSlotIfMissing(sapt: SaptamanaUI, sed: SelectieSedinta): void {
        // ── Găsim datele originale din slotsResponse ──────────────────────────────
        let sourceLocatie: string = '';
        let sourceLocatieId: number = sed.locatie_id;
        let sourceZiData: { date: string; day_of_week: number; slots: SlotDisponibil[] } | null = null;

        for (const loc of this.slotsResponse!.data.locatii) {
            for (const spec of loc.specialisti) {
                if (spec.specialist_id !== sed.specialist_id) continue;
                const ziSource = spec.dates.find(d => d.date === sed.date);
                if (ziSource) {
                    sourceLocatie = loc.denumire_locatie;
                    sourceLocatieId = loc.locatie_id;
                    sourceZiData = ziSource;
                }
            }
        }

        // ── Găsim sau creăm specialistul în sapt.specialistiDisponibili ───────────
        let spec = sapt.specialistiDisponibili.find(s => s.specialist_id === sed.specialist_id);

        if (!spec) {
            spec = {
                specialist_id: sed.specialist_id,
                nume_specialist: sed.nume_specialist,
                locatie_id: sourceLocatieId,
                denumire_locatie: sourceLocatie,
                zileDisponibile: [],
            };
            sapt.specialistiDisponibili.push(spec);
        } else {
            // Actualizăm denumire_locatie dacă era goală (din injecții anterioare incomplete)
            if (!spec.denumire_locatie && sourceLocatie) {
                spec.denumire_locatie = sourceLocatie;
            }
        }

        // ── Găsim sau creăm ziua ──────────────────────────────────────────────────
        let zi = spec.zileDisponibile.find(z => z.date === sed.date);

        if (!zi) {
            const dateObj = new Date(sed.date + 'T00:00:00');
            const jsDay = dateObj.getDay();
            const dayOfWeek = jsDay === 0 ? 7 : jsDay;

            zi = {
                date: sed.date,
                dateFormatted: this.formatZiData(sed.date, dayOfWeek),
                day_of_week: sourceZiData?.day_of_week ?? dayOfWeek,
                slots: [],
            };

            const insertIdx = spec.zileDisponibile.findIndex(z => z.date > sed.date);
            if (insertIdx === -1) {
                spec.zileDisponibile.push(zi);
            } else {
                spec.zileDisponibile.splice(insertIdx, 0, zi);
            }
        }

        // ── Adăugăm slotul dacă nu există deja ───────────────────────────────────
        const slotExista = zi.slots.some(s => s.slot_id === sed.slot.slot_id);
        if (slotExista) return;

        // Preferăm slotul original din slotsResponse (are time_end și duration complete)
        const slotOriginal = sourceZiData?.slots.find(s => s.slot_id === sed.slot.slot_id);
        zi.slots.push(slotOriginal ? { ...slotOriginal } : { ...sed.slot });
        zi.slots.sort((a, b) => a.time_from.localeCompare(b.time_from));
    }

    // ─── updateSlots API ──────────────────────────────────────────────────────

    callUpdateSlots(batch: { sedinta: SelectieSedinta; action: 'add' | 'delete' | null }[]): void {
        const sedinte = batch.map(({ sedinta, action }) => ({
            slot_id: sedinta.slot.slot_id,
            date: sedinta.date,
            action
        }));
        const payload = { contract_id: this.contractId!, sedinte }
        this.inregistrareProgramareService.updateSlots(payload).subscribe({
            error: err => console.error('Error updateSlots:', err)
        });
    }

    // ─── Helpers selecție ─────────────────────────────────────────────────────

    isSlotSelected(weekIndex: number, sedintaIdx: number, date: string, slotId: number): boolean {
        return this.selectii.get(weekIndex)?.sedinte.some(
            s => s.sedintaIndex === sedintaIdx && s.date === date && s.slot.slot_id === slotId
        ) ?? false;
    }

    isDayBlockedForCurrentSedinta(weekIndex: number, date: string): boolean {
        if (this.frecventa !== 2) return false;
        const sel = this.selectii.get(weekIndex);
        if (!sel) return false;
        const activIdx = this.getActiveSedintaIndex(weekIndex);
        const otherIdx = activIdx === 0 ? 1 : 0;
        return sel.sedinte.some(s => s.sedintaIndex === otherIdx && s.date === date);
    }

    isDayBeforeSedinta1(weekIndex: number, date: string): boolean {
        if (this.frecventa !== 2) return false;

        const activIdx = this.getActiveSedintaIndex(weekIndex);
        const sel = this.selectii.get(weekIndex);

        if (activIdx === 1) {
            // Selectăm ședința 2 → ziua nu poate fi înainte de ședința 1
            const sedinta1 = sel?.sedinte.find(s => s.sedintaIndex === 0);
            if (!sedinta1) return false;
            return date < sedinta1.date;
        }

        if (activIdx === 0) {
            // Selectăm ședința 1 → ziua nu poate fi după ședința 2
            const sedinta2 = sel?.sedinte.find(s => s.sedintaIndex === 1);
            if (!sedinta2) return false;
            return date > sedinta2.date;
        }

        return false;
    }

    getBlockedLabelForDay(weekIndex: number, date: string): string | null {
        if (this.isDayBlockedForCurrentSedinta(weekIndex, date)) {
            const activIdx = this.getActiveSedintaIndex(weekIndex);
            return `Zi ocupată de Ședința ${activIdx === 0 ? 2 : 1}`;
        }
        if (this.isDayBeforeSedinta1(weekIndex, date)) {
            const activIdx = this.getActiveSedintaIndex(weekIndex);
            if (activIdx === 1) return 'Zi anterioară Ședinței 1';
            if (activIdx === 0) return 'Zi ulterioară Ședinței 2';
        }
        return null;
    }

    getSelectedSedinta(weekIndex: number, sedintaIdx: number): SelectieSedinta | null {
        return this.selectii.get(weekIndex)?.sedinte.find(s => s.sedintaIndex === sedintaIdx) ?? null;
    }

    // ─── Progres ──────────────────────────────────────────────────────────────

    get nrSelectiiComplete(): number {
        let count = 0;
        for (const [, sel] of this.selectii) count += sel.sedinte.length;
        return count;
    }

    get isFormValid(): boolean {
        return this.nrSelectiiComplete >= this.nrSedinte;
    }

    get progressPercent(): number {
        return Math.min(100, Math.round((this.nrSelectiiComplete / this.nrSedinte) * 100));
    }

    onConfirma(): void {
        if (!this.isFormValid) return;

        const timeSlots: ConfirmaSelectieSlot[] = [];

        for (const [, sel] of this.selectii) {
            for (const s of sel.sedinte) {
                timeSlots.push({
                    slot_id: s.slot.slot_id,
                    date: s.date,
                    time_from: s.slot.time_from,
                    specialist_id: s.specialist_id,
                    nume_specialist: s.nume_specialist,
                });
            }
        }

        this.confirmaSelectie.emit({ timeSlots });
    }

    private emitSelectiiChange(): void {
        const sloturi: { slot_id: number; date: string }[] = [];
        for (const [, sel] of this.selectii) {
            for (const s of sel.sedinte) {
                sloturi.push({ slot_id: s.slot.slot_id, date: s.date });
            }
        }
        this.selectiiChange.emit(sloturi);
    }

    isUltimaSaptamanaValida(sapt: SaptamanaUI): boolean {
        const toateDatele = this.getAllDates(); // sortate ASC din backend
        if (!toateDatele.length) return true;

        const ultimaData = new Date(toateDatele[toateDatele.length - 1] + 'T00:00:00');
        const ultimaSapt = this.saptamani[this.saptamani.length - 1];

        // Dacă ultima dată cu sloturi din backend se află în ultima săptămână generată
        // → nu mai există rezervă → skip dezactivat pentru toate săptămânile
        return ultimaData >= ultimaSapt.dateStart && ultimaData <= ultimaSapt.dateEnd;
    }

    getMaxSedintePentruSaptamana(weekIndex: number): number {
        const dejaInAceasta = this.selectii.get(weekIndex)?.sedinte.length ?? 0;
        const selectateInAlte = this.nrSelectiiComplete - dejaInAceasta;
        const ramas = this.nrSedinte - selectateInAlte;
        return Math.min(this.frecventa, Math.max(0, ramas));
    }

    canAdaugaSedintaInSaptamana(weekIndex: number): boolean {
        return this.getMaxSedintePentruSaptamana(weekIndex) > (this.selectii.get(weekIndex)?.sedinte.length ?? 0);
    }

    isSlotInInterval(date: string): boolean {
        if (!this.dateEndSedinte) return true;
        return date <= this.dateEndSedinte;
    }

    getSedinteSortate(weekIndex: number): (SelectieSedinta & { denumire_locatie: string })[] {
        const sedinte = [...(this.selectii.get(weekIndex)?.sedinte ?? [])]
            .sort((a, b) => a.sedintaIndex - b.sedintaIndex);

        return sedinte.map(s => {
            // Căutăm locația din slotsResponse (sursa de adevăr)
            let denumire_locatie = '';
            for (const loc of this.slotsResponse?.data.locatii ?? []) {
                if (loc.specialisti.some(sp => sp.specialist_id === s.specialist_id)) {
                    denumire_locatie = loc.denumire_locatie;
                    break;
                }
            }
            return { ...s, denumire_locatie };
        });
    }
}